import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  X, Download, Check, Info, ChevronDown, ChevronUp, ArrowUpRight, AlertTriangle,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";

// Matches the IconBtn / Section pattern used by DetailSidebar so the export
// panel reads as a sibling pane, not a one-off modal.
function IconBtn({ children, title, onClick, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`p-2 rounded text-neutral-300 ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-white/5 hover:text-white"}`}
    >
      {children}
    </button>
  );
}

function Section({ title, children, right }) {
  return (
    <section>
      <div className="mb-3 flex items-end justify-between gap-3">
        <h3 className="text-[13px] font-semibold tracking-wide text-neutral-400 uppercase">
          {title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}

// Single chip pattern used for both "field" and "type" toggles. Active uses
// sky tint + a small check mark; inactive sits on the neutral surface. No
// native checkbox visible — clicking the chip toggles.
function ToggleChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors ${
        active
          ? "border-sky-500/40 bg-sky-500/15 text-sky-100"
          : "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:text-neutral-100"
      }`}
    >
      {active && <Check className="h-3 w-3" strokeWidth={3} />}
      <span>{children}</span>
    </button>
  );
}

// Small uppercase "Required" pill used in section headers / inline next to a
// must-fill control. Same ring-1 inset vocabulary as the dashboard's chips.
function RequiredPill() {
  return (
    <span className="inline-flex items-center text-[9.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30">
      Required
    </span>
  );
}

function RadioChip({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center rounded-md border px-2.5 py-1.5 text-[12.5px] transition-colors ${
        active
          ? "border-sky-500/40 bg-sky-500/15 text-sky-100"
          : "border-sky-900/40 bg-[#0a1224] text-neutral-300 hover:border-sky-700/50 hover:text-neutral-100"
      }`}
    >
      {children}
    </button>
  );
}

export default function ExportCSVPanel({
  open,
  onClose,
  onExport,
  credits = 0,
  defaultDomain = "",
  defaultStart = "",
  defaultEnd = "",
  query,
}) {
  const [domain, setDomain] = useState(query);
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);

  const [fields, setFields] = useState({
    hostname: false,
    ipAddress: false,
    osInfo: false,
    antivirus: false,
    malwarePath: false,
    countryCode: false,
  });

  const [types, setTypes] = useState({
    consumer: true,
    corporate: true,
  });

  const [isSubdomain, setIsSubdomain] = useState(null);

  useEffect(() => { setDomain(query ?? ""); }, [query]);

  function getJwtToken() {
    try {
      return (
        localStorage.getItem("jwtToken") ||
        localStorage.getItem("token") ||
        sessionStorage.getItem("jwtToken") ||
        window.WHITEINTEL_TOKEN ||
        ""
      );
    } catch {
      return "";
    }
  }

  const [exportLimit, setExportLimit] = useState(null);

  useEffect(() => {
    if (!open) return;
    const fetchLimit = async () => {
      try {
        const token = getJwtToken();
        const res = await fetch(
          "https://whiteintel.io/api/regular/app/v2/user/get_subscription.php",
          { method: "GET", headers: { Authorization: `Bearer ${token}` } },
        );
        if ([401, 403, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const data = await res.json();
        if (data?.success) setExportLimit(data.csv_export_limit ?? null);
      } catch (err) {
        console.error("Failed to fetch CSV export limit:", err);
      }
    };
    fetchLimit();
  }, [open]);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [infoOpen, setInfoOpen] = useState(false);

  useEffect(() => {
    if (open) {
      setError("");
      setSubmitting(false);
    }
  }, [open]);

  // Close on ESC — same behaviour as DetailSidebar.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const canExport = useMemo(() => {
    const hasType = types.consumer || types.corporate;
    const hasCredits = credits > 0;
    const datesOk =
      (!startDate && !endDate) ||
      (startDate && endDate && new Date(startDate) <= new Date(endDate));
    const hasDomain = domain.trim().length > 0;
    const subdomainAnswered = isSubdomain !== null;
    return hasType && hasCredits && datesOk && hasDomain && subdomainAnswered && !submitting;
  }, [types, credits, startDate, endDate, domain, isSubdomain, submitting]);

  // Explicit list of what's still missing, so the disabled CTA is helpful
  // instead of mysterious. Rendered as small amber chips above the footer
  // buttons; hidden when the form is complete.
  const missing = useMemo(() => {
    const list = [];
    if (!domain.trim()) list.push("Query");
    if (isSubdomain === null) list.push("Subdomain Yes/No");
    if (!startDate || !endDate) list.push("Date range");
    else if (new Date(startDate) > new Date(endDate)) list.push("Valid date order");
    if (!(types.consumer || types.corporate)) list.push("Data type");
    if (credits <= 0) list.push("Credits");
    return list;
  }, [domain, isSubdomain, startDate, endDate, types, credits]);

  const toggleField = (key) => setFields((f) => ({ ...f, [key]: !f[key] }));
  const toggleType = (key) => setTypes((t) => ({ ...t, [key]: !t[key] }));

  async function handleExport() {
    setError("");
    if (!startDate || !endDate) return setError("You must select date intervals.");
    if (!domain.trim()) return setError("Please provide a domain.");
    if (isSubdomain === null) return setError("Please specify whether the query is a subdomain.");
    if (!(types.consumer || types.corporate)) return setError("Select at least one data type.");
    if (credits <= 0) return setError("No export credits remaining.");
    if (startDate && endDate && new Date(startDate) > new Date(endDate))
      return setError("Start Date must be before End Date.");

    const payload = {
      query: domain.trim(),
      start_date: startDate || null,
      end_date: endDate || null,
      selectedFields: {
        hostname: !!fields.hostname,
        ipAddress: !!fields.ipAddress,
        osInfo: !!fields.osInfo,
        antivirus: !!fields.antivirus,
        malwarePath: !!fields.malwarePath,
        countryCode: !!fields.countryCode,
      },
      selectedDataTypes: {
        consumer: !!types.consumer,
        corporate: !!types.corporate,
      },
      is_subdomain: isSubdomain,
    };

    try {
      setSubmitting(true);
      await onExport?.(payload);
    } catch (e) {
      setError(e?.message || "Export failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className={`fixed inset-0 z-[80] ${open ? "" : "pointer-events-none"}`}
      role="dialog"
      aria-modal="true"
      aria-label="Export CSV"
    >
      {/* Backdrop — matches DetailSidebar. */}
      <div
        onClick={onClose}
        className={`absolute inset-0 bg-black/60 transition-opacity ${open ? "opacity-100" : "opacity-0"}`}
      />

      {/* Panel — same surface, border, shadow, and slide animation as
          DetailSidebar so the export pane reads as a sibling, not a stranger. */}
      <aside
        className={`absolute right-0 top-0 h-full w-full max-w-3xl flex flex-col border-l border-sky-900/40 bg-[#06101e] shadow-[0_0_50px_rgba(0,0,0,0.5)] transform transition-transform duration-300 ${
          open ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Header — small icon eyebrow + title + IconBtn close. */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-sky-900/30 shrink-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[13px] text-neutral-400">
              <Download className="h-4 w-4" />
              <span>Export</span>
            </div>
            <h2 className="mt-1 text-[16px] font-semibold tracking-tight text-neutral-100">
              CSV Export
            </h2>
          </div>
          <div className="flex items-center gap-2">
            <IconBtn title="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </IconBtn>
          </div>
        </div>

        {/* Error banner — same vocabulary as other sidebars. */}
        {error && (
          <div className="mx-5 mt-3 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-[12.5px] text-red-200 flex items-start justify-between gap-3">
            <span className="min-w-0">{error}</span>
            <button
              type="button"
              onClick={() => setError("")}
              className="text-[11px] text-neutral-400 hover:text-white shrink-0"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-5 py-5 space-y-6">
          {/* Credits — single calm row. Status pill on the right uses the
              dashboard's ring-1 inset vocabulary. */}
          <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70 p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-neutral-500">
                CSV export credits
              </div>
              <div className={`mt-1 text-[20px] font-semibold tabular-nums ${exportLimit > 0 ? "text-neutral-100" : "text-red-300"}`}>
                {exportLimit !== null ? exportLimit : "—"}
              </div>
              {exportLimit !== null && exportLimit <= 0 && (
                <Link
                  to="/upgrades"
                  className="mt-1 inline-flex items-center gap-1 text-[11.5px] text-sky-300 hover:text-sky-200"
                >
                  Upgrade your plan for more <ArrowUpRight className="h-3 w-3" />
                </Link>
              )}
            </div>
            <span
              className={`inline-flex items-center text-[10.5px] font-semibold uppercase tracking-wider rounded px-2 py-1 ring-1 ring-inset ${
                exportLimit > 0
                  ? "bg-emerald-500/10 text-emerald-300 ring-emerald-500/30"
                  : "bg-red-500/10 text-red-300 ring-red-500/30"
              }`}
            >
              {exportLimit > 0 ? "Available" : "Insufficient credits"}
            </span>
          </div>

          {/* Query */}
          <Section title="Query" right={<RequiredPill />}>
            <input
              type="text"
              placeholder={query}
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 placeholder:text-neutral-500 focus:border-sky-500/50"
            />
            <div className="mt-3 flex items-center gap-2 flex-wrap">
              <span className="text-[12px] text-neutral-400">Is the query a subdomain?</span>
              <RadioChip active={isSubdomain === true} onClick={() => setIsSubdomain(true)}>Yes</RadioChip>
              <RadioChip active={isSubdomain === false} onClick={() => setIsSubdomain(false)}>No</RadioChip>
              <RequiredPill />
            </div>
          </Section>

          {/* Date range */}
          <Section title="Date range" right={<RequiredPill />}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="block">
                <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 mb-1.5">Start</div>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 focus:border-sky-500/50"
                  style={{ colorScheme: "dark" }}
                />
              </label>
              <label className="block">
                <div className="text-[10.5px] uppercase tracking-wider text-neutral-500 mb-1.5">End</div>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full rounded-md text-[13px] py-2 px-3 outline-none bg-[#000414] border border-sky-900/40 text-neutral-100 focus:border-sky-500/50"
                  style={{ colorScheme: "dark" }}
                />
              </label>
            </div>
          </Section>

          {/* Data types */}
          <Section title="Data types" right={<RequiredPill />}>
            <div className="flex flex-wrap gap-2">
              <ToggleChip active={types.consumer}  onClick={() => toggleType("consumer")}>Consumer records</ToggleChip>
              <ToggleChip active={types.corporate} onClick={() => toggleType("corporate")}>Corporate records</ToggleChip>
            </div>
            <div className="mt-2 text-[11px] text-neutral-500">
              At least one type must be selected.
            </div>
          </Section>

          {/* Additional fields */}
          <Section title="Additional fields">
            <div className="flex flex-wrap gap-2">
              {[
                { key: "hostname",    label: "Hostname" },
                { key: "ipAddress",   label: "IP address" },
                { key: "osInfo",      label: "OS info" },
                { key: "antivirus",   label: "Antivirus" },
                { key: "malwarePath", label: "Malware path" },
                { key: "countryCode", label: "Country code" },
              ].map((f) => (
                <ToggleChip key={f.key} active={!!fields[f.key]} onClick={() => toggleField(f.key)}>
                  {f.label}
                </ToggleChip>
              ))}
            </div>
          </Section>

          {/* Plan limits — collapsible footnote, calm and out of the way. */}
          <div className="rounded-md border border-sky-900/30 bg-[#0a1224]/70">
            <button
              type="button"
              onClick={() => setInfoOpen((v) => !v)}
              className="w-full flex items-center justify-between px-4 h-10 text-[12px] text-neutral-300 hover:bg-sky-500/5 transition-colors"
            >
              <span className="inline-flex items-center gap-2">
                <Info className="h-3.5 w-3.5 text-sky-300/80" />
                <span className="uppercase tracking-wider text-[11px] text-neutral-300">Plan limits</span>
              </span>
              {infoOpen ? <ChevronUp className="h-3.5 w-3.5 text-neutral-400" /> : <ChevronDown className="h-3.5 w-3.5 text-neutral-400" />}
            </button>
            {infoOpen && (
              <div className="px-4 pb-4 pt-1 border-t border-sky-900/30 text-[12px] text-neutral-400 space-y-1">
                <div><span className="text-neutral-200 font-medium">Researcher</span> — 10 exports/month, max 2-month interval.</div>
                <div><span className="text-neutral-200 font-medium">Enterprise</span> — 10 exports/day, max 1-year interval.</div>
                <div><span className="text-neutral-200 font-medium">Threat Intelligence</span> — 500 exports/day, max 2-year interval.</div>
              </div>
            )}
          </div>

        </div>

        {/* "Missing" hint strip — only shown while the Export button is
            disabled, so the user knows exactly what to fix. Calm amber tone
            (same vocabulary as the Required pills). */}
        {!submitting && missing.length > 0 && (
          <div className="border-t border-sky-900/30 bg-[#0a1224]/60 px-5 py-2 shrink-0 flex items-center gap-2 flex-wrap text-[11px]">
            <span className="inline-flex items-center gap-1 text-amber-300">
              <AlertTriangle className="h-3 w-3" />
              <span className="uppercase tracking-wider text-[10.5px] font-semibold">Missing</span>
            </span>
            {missing.map((m) => (
              <span
                key={m}
                className="inline-flex items-center text-[10.5px] font-semibold uppercase tracking-wider rounded px-1.5 py-0.5 ring-1 ring-inset bg-amber-500/10 text-amber-300 ring-amber-500/30"
              >
                {m}
              </span>
            ))}
          </div>
        )}

        {/* Footer — matches DetailSidebar footer pattern. */}
        <div className="border-t border-sky-900/30 bg-[#0a1224]/80 backdrop-blur px-5 py-3 shrink-0 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              setDomain(defaultDomain);
              setStartDate(defaultStart);
              setEndDate(defaultEnd);
              setFields({ hostname: false, ipAddress: false, osInfo: false, antivirus: false, malwarePath: false, countryCode: false });
              setTypes({ consumer: true, corporate: true });
              setIsSubdomain(null);
              setError("");
            }}
            className="text-[12px] text-neutral-400 hover:text-neutral-200"
          >
            Reset
          </button>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={onClose}
              className="text-[12.5px] font-medium px-3 py-1.5 rounded-md border border-sky-900/40 text-neutral-300 hover:bg-sky-500/5"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleExport}
              disabled={!canExport}
              className={`inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-1.5 rounded-md border transition-colors ${
                canExport
                  ? "border-sky-500/40 bg-sky-500/15 text-sky-100 hover:bg-sky-500/25"
                  : "border-sky-900/40 bg-[#0a1224] text-neutral-500 cursor-not-allowed"
              }`}
            >
              <Download className="h-3.5 w-3.5" />
              {submitting ? "Exporting…" : "Export CSV"}
            </button>
          </div>
        </div>
      </aside>
    </div>
  );
}
